import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface Type {
  id: string;
  name: string;
  skin_path?: string;
  code: string;
}

interface Category {
  id: string;
  name: string;
  skin_path?: string;
  code: string;
  types: Type[];
}

interface Rarity {
  id: string;
  name: string;
}

interface Stat {
  id: string;
  name: string;
}

interface StaticDataState {
  types: Type[];
  categories: Category[];
  rarities: Rarity[];
  stats: Stat[];
  isLoading: {
    types: boolean;
    categories: boolean;
    rarities: boolean;
    stats: boolean;
  };
  error: {
    types: string | null;
    categories: string | null;
    rarities: string | null;
    stats: string | null;
  };
}

const initialState: StaticDataState = {
  types: [],
  categories: [],
  rarities: [],
  stats: [],
  isLoading: {
    types: false,
    categories: false,
    rarities: false,
    stats: false,
  },
  error: {
    types: null,
    categories: null,
    rarities: null,
    stats: null,
  },
};

const staticDataSlice = createSlice({
  name: 'staticData',
  initialState,
  reducers: {
    setTypes: (state, action: PayloadAction<Type[]>) => {
      state.types = action.payload;
      state.categories = state.categories.map((category) => ({
        ...category,
        types: action.payload.filter((type) => type.code.startsWith(category.code)),
      }));
      state.isLoading.types = false;
      state.error.types = null;
    },
    setCategories: (state, action: PayloadAction<Category[]>) => {
      state.categories = action.payload.map((category) => ({
        ...category,
        types: [],
      }));
      if (state.types.length > 0) {
        state.categories = state.categories.map((category) => ({
          ...category,
          types: state.types.filter((type) => type.code.startsWith(category.code)),
        }));
      }
      state.isLoading.categories = false;
      state.error.categories = null;
    },
    setRarities: (state, action: PayloadAction<Rarity[]>) => {
      state.rarities = action.payload;
      state.isLoading.rarities = false;
      state.error.rarities = null;
    },
    setStats: (state, action: PayloadAction<Stat[]>) => {
      state.stats = action.payload;
      state.isLoading.stats = false;
      state.error.stats = null;
    },
    setLoading: (
      state,
      action: PayloadAction<{ key: keyof StaticDataState['isLoading']; value: boolean }>
    ) => {
      state.isLoading[action.payload.key] = action.payload.value;
    },
    setError: (
      state,
      action: PayloadAction<{ key: keyof StaticDataState['error']; value: string | null }>
    ) => {
      state.error[action.payload.key] = action.payload.value;
    },
  },
});

export const { setTypes, setCategories, setRarities, setStats, setLoading, setError } =
  staticDataSlice.actions;

export default staticDataSlice.reducer;
